import { Link } from "@tanstack/react-router";
import { ChevronLeft, PackageCheck, PackagePlus, Truck } from "lucide-react";

import { useGetConversations, useGetCounts } from "@/api/inbox";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/language";
import { WalletCard } from "@/features/billing/wallet-card";
import { OfferBanner } from "@/features/offer/offer-banner";
import { ConversationAvatar } from "@/features/chat/conversation-avatar";
import {
  formatConversationTime,
  localizePreview,
  Num,
} from "@/features/chat/lib";
import { useSession } from "@/session";

/**
 * In-delivery vs delivered at a glance: two counts and a proportional
 * two-segment bar. Live via the counts query the realtime socket refreshes.
 */
function DeliveryStatsBar() {
  const { t } = useLanguage();
  const { data: counts } = useGetCounts();
  const undelivered = counts?.undelivered ?? 0;
  const delivered = counts?.delivered ?? 0;
  const total = undelivered + delivered;

  return (
    <Link
      to="/chats"
      className="tw:flex tw:flex-col tw:gap-3 tw:rounded-2xl tw:border tw:border-border tw:bg-card tw:p-4 tw:no-underline"
    >
      <div className="tw:flex tw:items-center tw:gap-4">
        <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2.5">
          <span className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-warning-soft tw:text-warning">
            <Truck className="tw:size-4.5" strokeWidth={1.75} />
          </span>
          <span className="tw:flex tw:min-w-0 tw:flex-col">
            <span className="tw:text-lg tw:font-bold tw:leading-tight tw:text-foreground">
              <Num>{undelivered}</Num>
            </span>
            <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
              {t("Mandoob:Chats.FilterUndelivered")}
            </span>
          </span>
        </span>
        <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2.5">
          <span className="tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-success-soft tw:text-success-foreground">
            <PackageCheck className="tw:size-4.5" strokeWidth={1.75} />
          </span>
          <span className="tw:flex tw:min-w-0 tw:flex-col">
            <span className="tw:text-lg tw:font-bold tw:leading-tight tw:text-foreground">
              <Num>{delivered}</Num>
            </span>
            <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
              {t("Mandoob:Delivery.StatusDelivered")}
            </span>
          </span>
        </span>
      </div>
      <div className="tw:flex tw:h-1.5 tw:gap-0.5 tw:overflow-hidden tw:rounded-full tw:bg-muted">
        {undelivered > 0 && (
          <span
            className="tw:rounded-full tw:bg-warning tw:transition-[width]"
            style={{ width: `${(undelivered / total) * 100}%` }}
          />
        )}
        {delivered > 0 && (
          <span
            className="tw:rounded-full tw:bg-success tw:transition-[width]"
            style={{ width: `${(delivered / total) * 100}%` }}
          />
        )}
      </div>
    </Link>
  );
}

export function HomePage() {
  const { t, dir, language } = useLanguage();
  const session = useSession();
  const recentQuery = useGetConversations({ MaxResultCount: 3 });
  const recent = recentQuery.data?.items ?? [];

  return (
    <div dir={dir} className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
      <div className="tw:mx-auto tw:flex tw:w-full tw:flex-col tw:gap-5 tw:px-4 tw:pb-6 tw:pt-[calc(20px+env(safe-area-inset-top))] tw:md:max-w-5xl tw:md:gap-6 tw:md:px-8 tw:md:pt-8">
        <OfferBanner variant="app" />

        <header>
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            {t("Mandoob:Home.Greeting")}
          </p>
          <h1 className="tw:m-0 tw:text-2xl tw:font-bold">
            {session?.driver.name ?? session?.userName ?? ""}
          </h1>
        </header>

        <WalletCard />

        <DeliveryStatsBar />

        <Button
          asChild
          size="lg"
          className="tw:w-full tw:md:w-auto tw:md:self-start"
        >
          <Link to="/chats/new">
            <PackagePlus className="tw:size-5" />
            {t("Mandoob:NewDelivery.Title")}
          </Link>
        </Button>

        <section className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:flex tw:items-center tw:justify-between">
            <h2 className="tw:m-0 tw:text-base tw:font-semibold">
              {t("Mandoob:Home.RecentActivity")}
            </h2>
            <Link
              to="/chats"
              className="tw:inline-flex tw:items-center tw:gap-0.5 tw:text-sm tw:font-medium tw:text-brand tw:no-underline tw:hover:underline"
            >
              {t("Mandoob:Home.AllChats")}
              <ChevronLeft className="tw:size-4 tw:ltr:-scale-x-100" />
            </Link>
          </div>

          {recentQuery.isLoading ? (
            <div className="tw:flex tw:flex-col tw:gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="tw:flex tw:items-center tw:gap-3 tw:p-2"
                >
                  <Skeleton className="tw:size-10 tw:rounded-full" />
                  <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1.5">
                    <Skeleton className="tw:h-4 tw:w-1/3" />
                    <Skeleton className="tw:h-3 tw:w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : recent.length === 0 ? (
            <p className="tw:m-0 tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-8 tw:text-center tw:text-sm tw:text-muted-foreground">
              {t("Mandoob:Home.NoActivity")}
            </p>
          ) : (
            <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0">
              {recent.map((conversation) => (
                <li key={conversation.id}>
                  <Link
                    to="/chats/$conversationId"
                    params={{ conversationId: conversation.id }}
                    className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:p-2.5 tw:no-underline tw:transition-colors tw:hover:bg-muted/60"
                  >
                    <ConversationAvatar
                      name={conversation.name}
                      trackingNumber={conversation.trackingNumber}
                      phone={conversation.phoneNumber}
                      size={10}
                    />
                    <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                      <span className="tw:truncate tw:text-base tw:font-medium tw:text-foreground">
                        {conversation.name}
                      </span>
                      <span className="tw:truncate tw:text-sm tw:text-muted-foreground">
                        {localizePreview(conversation.preview, t)}
                      </span>
                    </span>
                    <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
                      {formatConversationTime(
                        conversation.lastMessageAt,
                        t,
                        language,
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
